// One-click export of a stored analysis: a meta file, then one file per trace
// block (every input and output the Data tab shows), per-step details for fields
// no block covers, any uploaded screenshots, the candles that followed the
// analysis (on its timeframe and 2 timeframes lower, over the replay horizon)
// and the system's own replay verdicts — zipped under
// `A<seq>_<user>_<pair>_<engine>`.
import { v0ImageUrl } from 'api/analysisApi';
import { getAnalysisBacktest } from 'api/backtestApi';
import { getAnalysisCandles } from 'api/historyApi';
import type { DebugImage } from 'types/analysis';
import type { AnalysisAuditDetail } from 'types/history';
import { BLOCK_FIELDS, blockExtension, blockText, isBlockEmpty, traceBlocks } from './traceBlocks';

/** Human-facing run number, e.g. "A1234". */
export function analysisNo(seq: number | null | undefined): string {
	return seq == null ? '—' : `A${seq}`;
}

/** Filename-safe segment. `_` is the name's separator, so it is folded to `-`. */
function slug(value: string | null | undefined, fallback: string): string {
	const s = (value ?? '').replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
	return s || fallback;
}

/** `A1234_rpverify_BTCUSDT_enforcer` — shared by the zip and every file in it. */
export function bundleBaseName(detail: Pick<AnalysisAuditDetail, 'seq' | 'id' | 'username' | 'asset' | 'engine'>): string {
	// Pre-migration rows have no seq; fall back to the UUID prefix so names stay unique.
	const no = detail.seq != null ? `A${detail.seq}` : `A-${detail.id.slice(0, 8)}`;
	return [no, slug(detail.username, 'anon'), slug(detail.asset, 'pair'), slug(detail.engine, 'engine')].join('_');
}

const json = (value: unknown) => JSON.stringify(value, null, 2);

const STEPS = ['step1', 'step2', 'step3'] as const;

/** Timeframe rungs down for the intrabar view: 1h → 15m, 4h → 30m. */
const LOWER_STEPS = 2;

/**
 * The market after the analysis, as up to two files. The count is the server's
 * replay horizon (39 at the default rules), so an offline script sees every
 * candle the system's verdict was computed on; the first candle is the partial
 * stub of the bar the analysis ran in (`partial: true`). Never throws: a failed
 * fetch becomes `candles_note.txt`, so the rest of the bundle still downloads.
 */
async function candleFiles(detail: AnalysisAuditDetail, base: string): Promise<Record<string, string>> {
	const files: Record<string, string> = {};
	try {
		const res = await getAnalysisCandles(detail.id, { lowerSteps: LOWER_STEPS });
		const { candles, lower_candles, lower_timeframe, ...meta } = res;
		const tf = slug(res.timeframe, 'tf');
		const closed = candles.filter((c) => !c.partial).length;
		if (candles.length > 0) {
			files[`${base}_candles_${tf}_next${res.requested}.json`] = json({ ...meta, timeframe: res.timeframe, count: candles.length, candles });
		}
		if (lower_timeframe && lower_candles.length > 0) {
			files[`${base}_candles_${slug(lower_timeframe, 'tf')}_same-span.json`] = json({
				...meta,
				timeframe: lower_timeframe,
				parent_timeframe: res.timeframe,
				count: lower_candles.length,
				candles: lower_candles,
			});
		}
		const notes = [
			res.error,
			candles.length === 0 && !res.error ? 'No closed candles after this analysis yet.' : null,
			candles.length > 0 && !res.complete ? `Only ${closed} of ${res.requested} candles have closed so far.` : null,
			res.first_bar_unverified ? 'The first candle could not be cut at the analysis cutoff (no sub-candles); it is the whole bar and may include price from before the analysis.' : null,
		].filter(Boolean);
		if (notes.length) files[`${base}_candles_note.txt`] = notes.join('\n');
	} catch (e) {
		files[`${base}_candles_note.txt`] = `Candle request failed: ${e instanceof Error ? e.message : String(e)}`;
	}
	return files;
}

/**
 * The system's own replay of the run's proposals (verdict, fill, exit, R and
 * the cancellation reason per trade) under the stored rules — what an external
 * outcome script reconciles against. Candles are left out: the candles files
 * above carry them, with volume. Never throws.
 */
async function replayFiles(detail: AnalysisAuditDetail, base: string): Promise<Record<string, string>> {
	try {
		const { candles: _candles, ...replay } = await getAnalysisBacktest(detail.id, {});
		return { [`${base}_backtest_replay.json`]: json(replay) };
	} catch (e) {
		return { [`${base}_backtest_note.txt`]: `Replay request failed: ${e instanceof Error ? e.message : String(e)}` };
	}
}

/** Screenshot bytes, or null when it cannot be fetched (not stored / storage down). */
async function fetchImage(img: DebugImage): Promise<Uint8Array | null> {
	const src = img.object_key ? v0ImageUrl(img.object_key) : img.data_url;
	if (!src) return null;
	try {
		// The v0-image endpoint is auth-optional (plain <img> loads use it), and
		// fetch() also decodes legacy data: URLs.
		const res = await fetch(src);
		if (!res.ok) return null;
		return new Uint8Array(await res.arrayBuffer());
	} catch {
		return null;
	}
}

/**
 * Files for the bundle, keyed by name. Blocks come from the same `traceBlocks`
 * the Data tab renders, so the zip holds exactly what the per-block ↓ buttons
 * give — for 1-step (v0/v1), 2-step (claude) and 3-step (enforcer) engines alike.
 * Nothing stored is dropped: fields no block covers go to `stepN_details.json`.
 */
export async function buildTraceBundle(detail: AnalysisAuditDetail): Promise<{ base: string; files: Record<string, string | Uint8Array> }> {
	const base = bundleBaseName(detail);
	const { input: trace, output, error, ...summary } = detail;
	const files: Record<string, string | Uint8Array> = {};
	// Started first: both are network round-trips to the exchange, independent of the trace.
	const candles = candleFiles(detail, base);
	const replay = replayFiles(detail, base);

	files[`${base}_meta.json`] = json({
		analysis_no: analysisNo(detail.seq),
		...summary,
		total_elapsed_s: trace?.total_elapsed_s ?? null,
	});

	if (trace) {
		const blocks = traceBlocks(trace);
		for (const step of STEPS) {
			for (const b of blocks[step]) {
				if (isBlockEmpty(b.kind, b.value)) continue;
				files[`${base}_${b.key}.${blockExtension(b.kind)}`] = blockText(b.kind, b.value);
			}
			const raw = trace[step] as Record<string, unknown> | undefined;
			if (!raw) continue;
			const rest = Object.fromEntries(Object.entries(raw).filter(([k]) => !BLOCK_FIELDS[step].includes(k)));
			files[`${base}_${step}_details.json`] = json(rest);
		}

		const images = trace.step1.images ?? [];
		const bytes = await Promise.all(images.map(fetchImage));
		images.forEach((img, i) => {
			const body = bytes[i];
			if (!body) return;
			const dot = img.filename.lastIndexOf('.');
			const ext = dot > 0 ? img.filename.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
			const stem = slug(dot > 0 ? img.filename.slice(0, dot) : img.filename, 'image');
			files[`${base}_step1_input_image${i + 1}_${stem}${ext ? `.${ext}` : ''}`] = body;
		});
	} else if (output) {
		// No trace persisted (non-debug runs): the output is all there is.
		files[`${base}_output_analysis.json`] = json(output);
	}
	if (error) files[`${base}_error.txt`] = error;
	Object.assign(files, await candles, await replay);

	return { base, files };
}
